'use client'

import { memo, useState, useCallback, useEffect, useRef } from 'react'
import Image from 'next/image'
import { X, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getImageUrl } from '@/lib'
import type { PickerSelection } from './ImageLibraryModal'

interface SelectionTrayProps {
  /** Current selections (with instanceId for duplicate support) */
  selections: PickerSelection[]
  /** Maximum number of selections allowed */
  maxSelections: number
  /** Called when an item is removed (by instanceId) */
  onRemove: (instanceId: string) => void
  /** Whether the tray is in a loading state */
  isLoading?: boolean
}

/**
 * SelectionTrayItem
 *
 * Individual selection item with hover/tap-to-reveal remove button.
 * Desktop: hover to show X
 * Mobile: tap image to show X, tap elsewhere to hide, tap X to remove
 */
const SelectionTrayItem = memo(function SelectionTrayItem({
  selection,
  onRemove,
  isLoading,
  isActive,
  onActivate,
}: {
  selection: PickerSelection
  onRemove: (instanceId: string) => void
  isLoading: boolean
  isActive: boolean
  onActivate: () => void
}) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      // First tap activates to show X
      if (!isActive) {
        onActivate()
      }
    },
    [isActive, onActivate]
  )

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onRemove(selection.instanceId)
    },
    [onRemove, selection.instanceId]
  )

  return (
    <div
      className="relative shrink-0 group cursor-pointer"
      onClick={handleClick}
    >
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 border-primary bg-muted relative">
        <Image
          src={getImageUrl(selection.imageUrl, 'thumbnail')}
          alt="Selected"
          width={64}
          height={64}
          className="w-full h-full object-cover"
          unoptimized
        />
        {/* Dark gradient overlay - visible on hover (desktop) or active (mobile) */}
        <div
          className={cn(
            'absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20',
            'transition-opacity duration-200',
            // Desktop: show on hover, Mobile: show when active
            'opacity-0 group-hover:opacity-100',
            isActive && 'opacity-100'
          )}
        />
        {/* Centered remove button */}
        <button
          onClick={handleRemove}
          disabled={isLoading}
          className={cn(
            'absolute inset-0 m-auto w-7 h-7 rounded-full',
            'bg-red-500 text-white flex items-center justify-center',
            'transition-all duration-200',
            'hover:bg-red-600 hover:scale-110',
            'disabled:opacity-50 disabled:hover:scale-100',
            'shadow-lg',
            // Desktop: show on hover, Mobile: show when active
            'opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100',
            // Disable pointer events when not visible (mobile tap protection)
            'pointer-events-none group-hover:pointer-events-auto',
            isActive && 'opacity-100 scale-100 pointer-events-auto'
          )}
          title="Remove"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  )
})

/**
 * SelectionTray
 *
 * Collapsible horizontal strip showing selected images for picker mode.
 * Supports duplicate selections (same image can appear multiple times).
 * Each item has a centered X button to remove that specific instance.
 */
export const SelectionTray = memo(function SelectionTray({
  selections,
  maxSelections,
  onRemove,
  isLoading = false,
}: SelectionTrayProps) {
  const remainingSlots = maxSelections - selections.length
  const isComplete = selections.length === maxSelections
  const isEmpty = selections.length === 0

  // Collapsed state - start expanded
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Track which item is "active" (showing X on mobile)
  const [activeId, setActiveId] = useState<string | null>(null)
  const trayRef = useRef<HTMLDivElement>(null)

  // Click outside to dismiss active state
  useEffect(() => {
    if (!activeId) return

    const handleClickOutside = (e: MouseEvent) => {
      if (trayRef.current && !trayRef.current.contains(e.target as Node)) {
        setActiveId(null)
      }
    }

    const handleScroll = () => {
      setActiveId(null)
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('scroll', handleScroll, true)
    }, 10)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [activeId])

  // Clear active state when an item is removed
  const handleRemove = useCallback(
    (instanceId: string) => {
      setActiveId(null)
      onRemove(instanceId)
    },
    [onRemove]
  )

  // Handle click on tray background to dismiss
  const handleTrayClick = useCallback(() => {
    setActiveId(null)
  }, [])

  // Toggle collapsed state
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev)
    setActiveId(null)
  }, [])

  return (
    <div ref={trayRef} className="border-t bg-muted/30">
      {/* Collapsible header tab */}
      <button
        onClick={toggleCollapsed}
        className={cn(
          'w-full px-4 sm:px-6 py-2 flex items-center justify-between',
          'hover:bg-muted/50 transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset'
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            Selected Images
          </span>
          <span
            className={cn(
              'text-xs px-2 py-0.5 rounded-full',
              isComplete
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : isEmpty
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-primary/10 text-primary'
            )}
          >
            {selections.length}/{maxSelections}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isCollapsed ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Collapsible content */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isCollapsed ? 'max-h-0' : 'max-h-52 sm:max-h-56'
        )}
      >
        <div className="px-4 sm:px-6 pb-3">
          {isEmpty ? (
            <p className="text-sm text-muted-foreground py-2">
              Select up to {maxSelections} photo{maxSelections !== 1 ? 's' : ''}{' '}
              from your library
            </p>
          ) : (
            <>
              {/* Selection grid with vertical scroll */}
              <div
                className="flex flex-wrap gap-2 max-h-28 sm:max-h-36 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent"
                onClick={handleTrayClick}
              >
                {/* Selected items */}
                {selections.map((selection) => (
                  <SelectionTrayItem
                    key={selection.instanceId}
                    selection={selection}
                    onRemove={handleRemove}
                    isLoading={isLoading}
                    isActive={activeId === selection.instanceId}
                    onActivate={() => setActiveId(selection.instanceId)}
                  />
                ))}

                {/* Remaining count indicator */}
                {remainingSlots > 0 && (
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                    <span className="text-xs text-muted-foreground font-medium">
                      +{remainingSlots}
                    </span>
                  </div>
                )}
              </div>

              {/* Helper text */}
              {!isComplete && (
                <p className="text-xs text-muted-foreground mt-2">
                  Click photos above to add. Same photo can be selected multiple
                  times.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
})
